import { type ReactNode } from 'react';

import {
  faCircleQuestion,
  faClose,
  faPause,
  faPlay,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';
import { motion } from 'framer-motion';
import { useTranslations } from 'use-intl';

import Button from './ui.button';

interface Props {
  handlePlayPauseClick?: () => void;
  showPlayIcon?: boolean;
  playPauseAriaLabel?: string;
  children: ReactNode;
  className?: string;
  isHelpExpanded?: boolean;
  onHelpExpandedChange?: (expanded: boolean) => void;
  isConversationOver?: boolean;
  compact?: boolean;
}

export function VoiceOrbDock({
  handlePlayPauseClick,
  showPlayIcon = false,
  playPauseAriaLabel,
  children,
  className,
  isHelpExpanded = false,
  onHelpExpandedChange,
  isConversationOver = false,
  compact = false,
}: Props) {
  const t = useTranslations('Voice');

  const handleHelpClick = () => {
    onHelpExpandedChange?.(true);
  };

  const handleCloseHelp = () => {
    onHelpExpandedChange?.(false);
  };

  return (
    <div
      className={cn('relative flex items-center justify-center', className)}
      data-test='voice-orb-dock'
      data-compact={compact}
    >
      {!compact && (
        <svg
          width='240'
          height='148'
          viewBox='0 0 240 148'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-18'
          aria-hidden='true'
        >
          <path
            d='M120 0C148.584 0 173.049 17.6368 183.106 42.622C188.468 55.9427 199.641 68 214 68C228.359 68 240 79.6406 240 94V108C240 130.091 222.091 148 200 148H40C17.9086 148 0 130.091 0 108V94C0 79.6406 11.6406 68 26 68C40.3594 68 51.5317 55.9428 56.8936 42.622C66.9508 17.6368 91.4157 0 120 0Z'
            fill='#0B0C0D'
          />
        </svg>
      )}

      {/* Action Buttons and Orb Container */}
      <div
        className={cn(
          'relative z-10 flex gap-3',
          compact ? 'items-center' : 'items-end'
        )}
      >
        {handlePlayPauseClick && (
          <Button
            variant='ghost'
            size='icon'
            className={cn(
              'size-10 rounded-full bg-gray-50/12 text-white hover:enabled:bg-gray-50/25',
              !compact && 'mb-3.5'
            )}
            onClick={handlePlayPauseClick}
            aria-label={playPauseAriaLabel ?? t('playOrPauseAria')}
            disabled={isConversationOver}
            data-test='handsfree-pause-resume-button'
          >
            <FontAwesomeIcon
              icon={showPlayIcon ? faPlay : faPause}
              className='size-4'
            />
          </Button>
        )}

        {/* Center Orb Slot */}
        <div
          className={cn(
            'relative flex items-center justify-center',
            !compact && 'mb-2'
          )}
          data-test='voice-orb-dock-orb-slot'
        >
          {children}
        </div>

        {/* Right Action Button */}
        <div
          className={cn('relative flex items-center', !compact && 'mb-3.5')}
          data-test='voice-orb-dock-right-action'
        >
          {!isHelpExpanded ? (
            <Button
              variant='ghost'
              size='icon'
              className='size-10 rounded-full bg-gray-50/12 text-white hover:enabled:bg-gray-50/25'
              onClick={handleHelpClick}
              aria-label={t('getHelpAria')}
              data-test='handsfree-help-button'
            >
              <FontAwesomeIcon icon={faCircleQuestion} className='size-4' />
            </Button>
          ) : (
            <>
              {/* Mobile only: full help text in popover above button */}
              <div
                className='absolute bottom-full left-1/2 z-20 mb-2 flex -translate-x-1/2 items-center gap-2 rounded-2xl bg-gray-950 px-4 py-2.5 sm:hidden'
                data-test='handsfree-help-popover-mobile'
              >
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  className='size-4 shrink-0 text-white'
                  aria-hidden='true'
                />
                <span className='text-sm whitespace-nowrap text-white'>
                  {t('sayNextToProceed')}
                </span>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-6 shrink-0 rounded-full text-white hover:enabled:bg-gray-50/25'
                  onClick={handleCloseHelp}
                  aria-label={t('closeHelpAria')}
                  data-test='handsfree-help-close-button'
                >
                  <FontAwesomeIcon icon={faClose} className='size-3' />
                </Button>
              </div>
              <Button
                variant='ghost'
                size='icon'
                className='size-10 rounded-full bg-gray-50/12 text-white hover:enabled:bg-gray-50/25 sm:hidden'
                onClick={handleCloseHelp}
                aria-label={t('closeHelpAria')}
                data-test='handsfree-help-button'
              >
                <FontAwesomeIcon icon={faCircleQuestion} className='size-4' />
              </Button>
              {/* Desktop */}
              <div className='hidden sm:block sm:size-10' aria-hidden='true' />
              <motion.div
                className='absolute top-0 left-0 hidden items-center gap-2 overflow-hidden rounded-full bg-gray-50/12 sm:flex'
                initial={{ width: 40 }}
                animate={{ width: 'auto' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                data-test='handsfree-say-next-pill'
              >
                <motion.div
                  className='flex items-center gap-2 px-3 py-2'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className='size-4 shrink-0 text-white'
                  />
                  <span className='text-sm whitespace-nowrap text-white'>
                    {t('sayNextToProceed')}
                  </span>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-6 rounded-full text-white hover:enabled:bg-gray-50/25'
                    onClick={handleCloseHelp}
                    aria-label={t('closeHelpAria')}
                    data-test='handsfree-help-close-button'
                  >
                    <FontAwesomeIcon icon={faClose} className='size-3' />
                  </Button>
                </motion.div>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
